import {
  DataGrid,
  DataGridActions,
  DataGridColumns,
  DataGridSections,
  DataGridSelectedActions,
} from '@components/DataGrid/DataGrid'
import { DataGridPerson } from '@components/DataGrid/DataGridPerson'
import { useLocation, useNavigate } from 'react-router-dom'
import { RoutePaths } from '@interfaces/routePaths'
import {
  ActionIcon,
  Box,
  Button,
  Menu,
  Stack,
  Text,
  ThemeIcon,
} from '@mantine/core'
import classes from '@styles/record.module.css'
import {
  IconChevronDown,
  IconDots,
  IconFileTypeDoc,
  IconFileTypeDocx,
  IconFileTypeJpg,
  IconFileTypePdf,
  IconFileTypeTxt,
} from '@tabler/icons-react'
import { randomArrayItem } from '@utils/randomArrayItem'
import { DataGridList } from '@components/DataGrid/DataGridList'
import { ReactNode } from 'react'
import cn from 'classnames'

export interface FilesProps {
  fullWidth?: boolean
  state?: { history: any }
}

export const Files = (props: FilesProps) => {
  const { fullWidth, state } = props

  const navigate = useNavigate()
  const location = useLocation()

  const handleNavigate = () =>
    navigate(
      `../${RoutePaths.RECORD}/123/${RoutePaths.RECORD_HOME}`,
      location?.state?.history
        ? { state: location?.state }
        : state
        ? { state }
        : {},
    )

  const data = Array(30)
    .fill(0)
    .map((_, i) => {
      return {
        id: `invite-${i}`,
        name: randomArrayItem([
          'Health Examination',
          'Enrollment verification',
          'Utility bill',
          'Lease',
          'Income and expenses',
          'SNAP application',
          'Order of protection',
          'Driver’s license',
        ]),
        category: randomArrayItem([
          'Health',
          'Verification',
          'Application',
          'Legal',
          'Identification',
        ]),
        subcategory: 'Something',
        shared: (
          <Stack gap={0}>
            <Text size="xs">5:59pm</Text>
            <Text size="xs" c="dimmed">
              Mar 11
            </Text>
          </Stack>
        ),
        uploadedBy: (
          <DataGridPerson
            name="Alfredo Carrillo"
            status="online"
            onClick={handleNavigate}
          />
        ),
        repository: randomArrayItem([
          'Alfredo Carrillo',
          'Community',
          'Public',
        ]),
        tags: (
          <DataGridList title="Tags" data={['Some', 'Another', 'Another']} />
        ),
        type: (
          <ThemeIcon variant="transparent" color="dark.3">
            {
              randomArrayItem([
                <IconFileTypeDocx size={22} />,
                <IconFileTypePdf size={22} />,
                <IconFileTypeJpg size={22} />,
                <IconFileTypeTxt size={22} />,
                <IconFileTypeDoc size={22} />,
              ]) as ReactNode
            }
          </ThemeIcon>
        ),
      }
    })

  const fullWidthCols = fullWidth
    ? [
        {
          label: 'File name',
          prop: 'name',
          defaultSort: true,
          pinned: true,
        },
        {
          label: 'Repository',
          prop: 'repository',
        },
      ]
    : [
        {
          label: 'File name',
          prop: 'name',
          defaultSort: true,
          pinned: true,
        },
      ]

  const columns: DataGridColumns[] = [
    ...fullWidthCols,
    {
      label: 'Category',
      prop: 'category',
      pinned: true,
    },
    {
      label: 'Subcategory',
      prop: 'subcategory',
    },
    {
      label: 'Uploaded by',
      prop: 'uploadedBy',
    },
    {
      label: 'Tags',
      prop: 'tags',
    },
    {
      label: 'Type',
      prop: 'type',
    },
    {
      label: 'Shared',
      prop: 'shared',
    },
  ]

  const sections: DataGridSections[] = [
    {
      label: 'All Files',
      href: '#',
      default: true,
    },
    {
      label: 'Private',
      href: '#',
    },
    {
      label: 'Community',
      href: '#',
    },
    {
      label: 'Public',
      href: '#',
    },
  ]

  const actions: DataGridActions[] = [
    {
      label: 'Open',
      onClick: (item: any) => console.log('Action'),
    },
    {
      label: 'Edit details',
      onClick: (item: any) => console.log('Action'),
    },
    {
      label: 'Download',
      onClick: (item: any) => console.log('Action'),
    },
    {
      label: 'Add tags',
      onClick: (item: any) => console.log('Action'),
    },
    {
      label: 'Remove tags',
      color: 'red',
      onClick: (item: any) => console.log('Action'),
    },
    {
      label: 'Remove file',
      color: 'red',
      onClick: (item: any) => console.log('Action'),
    },
  ]

  const selectedActions: DataGridSelectedActions[] = [
    {
      label: 'Edit',
      onClick: (items: any) => console.log('Selected Actions: Edit'),
    },
    {
      label: 'Tags',
      onClick: (items: any) => console.log('Selected Actions: Tags'),
      component: (
        <div>
          <Menu position="bottom-start">
            <Menu.Target>
              <Button
                variant="default"
                rightSection={<IconChevronDown size={18} />}
              >
                Tags
              </Button>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Item>Add tags</Menu.Item>
              <Menu.Item color="red">Remove tags</Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </div>
      ),
    },
    {
      label: 'Options',
      onClick: (items: any) => {},
      component: (
        <div>
          <Menu>
            <Menu.Target>
              <ActionIcon variant="default" radius="xl" aria-label="Options">
                <IconDots
                  style={{ width: '70%', height: '70%' }}
                  stroke={1.5}
                />
              </ActionIcon>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Item>Download</Menu.Item>
              <Menu.Item color="red">Remove file</Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </div>
      ),
    },
  ]

  return (
    <Box className={cn({ [classes.Record__content]: !fullWidth })}>
      <DataGrid
        data={data}
        columns={columns}
        sections={sections}
        actions={actions}
        results={data.length}
        buttons={
          <div>
            <Menu position="bottom-end">
              <Menu.Target>
                <Button rightSection={<IconChevronDown size={18} />}>
                  Upload File
                </Button>
              </Menu.Target>
              <Menu.Dropdown w={138}>
                <Menu.Item>Private</Menu.Item>
                <Menu.Item>Community</Menu.Item>
                <Menu.Item>Public</Menu.Item>
              </Menu.Dropdown>
            </Menu>
          </div>
        }
        selectedActions={selectedActions}
        page={1}
      />
    </Box>
  )
}
